import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { MatSelectChange } from '@angular/material/select'

import { ActivatedRoute, Router } from '@angular/router'
import { ResultCode } from '../../../domain/authentication.model'
import { PaymentSources } from '../../../enums/enums'
import { ClientDataType } from '../../../services/cart-api.service'
import { CartService, ICartGroup } from '../../../services/cart.service'
import { CIDService } from '../../../services/cid.service'
import { EnvironmentService } from '../../../services/environment.service'
import { NotificationService } from '../../../services/notification.service'
import { CurrencySignType, I18nService, LanguagesTypes, LgType } from '../../../services/prod/i18n.service'

enum PaymentSystemType {
  webmoney = 0,
  yandexmoney = 1,
  visaAndMasterCard = 2,
  qiwi = 4,
  paypalRUB = 5,
  paypalUSD = 6
}

type PaymentType = {
  type: PaymentSystemType
  title: string
}

@Component({
  selector: 'bmcore-confirm-order-form',
  templateUrl: './bmcore-confirm-order-form.component.html',
  styleUrls: ['./bmcore-confirm-order-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BmcoreConfirmOrderFormComponent implements OnInit {
  @Input() group: ICartGroup
  @Input() paymentSource: PaymentSources
  @Input() widgetId: number

  @Output() hideSpinnerEvent = new EventEmitter()

  confirmOrderForm: UntypedFormGroup
  isPhoneField = false
  initForm = false

  lg: LgType
  lang: LanguagesTypes
  currencySign: CurrencySignType

  payments: PaymentType[] = []
  paymentType: number
  paymentPageLoading = false

  clientData: ClientDataType

  constructor(
    private i18nService: I18nService,
    private cartService: CartService,
    private cidService: CIDService,
    private environmentService: EnvironmentService,
    private notificationService: NotificationService,
    private formBuilder: UntypedFormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.currencySign = this.i18nService.getCurrencySign()
    this.lg = this.i18nService.getLanguageForPage()
    this.getClientData()
    this.getPayments()
    this.getPageLangValue()
  }

  getClientData() {
    this.cartService.getClientData().then((data: ClientDataType) => {
      this.clientData = data

      this.hideSpinnerEvent.emit()

      this.initForm = true

      this.confirmOrderForm = this.formBuilder.group({
        email: [],
        fullName: [],
        passport: [],
        phone: []
      })
    })
  }

  getPayments() {
    this.cartService.getPaymentsTypes(this.group.beatmakerId).then((res: PaymentType[]) => {
      this.payments = res
      if (this.payments.length) {
        this.paymentType = this.payments[0].type
      }
      // Set PAYPAL as default payment type for english lang; Yandex money to russian
      // this.paymentType = isEng ? PaymentSystemType.paypalUSD : PaymentSystemType.yandexmoney;
    })
  }

  getIsEng() {
    return this.i18nService.isEng()
  }

  changePayment(matEvent: MatSelectChange) {
    const paymentType = matEvent.value

    if (paymentType === PaymentSystemType.qiwi) {
      this.isPhoneField = true
    } else {
      // If you selected a payment system not equal to Qiwi, you need to nullify the phone field
      this.isPhoneField = false
      this.confirmOrderForm.controls['phone'].setErrors(null)
    }
  }

  getPageLangValue() {
    this.lang = this.i18nService.detectInitLanguage() === 'en' ? 'en' : 'ru'
  }

  async onSubmit() {
    // this.paymentType !== undefined: I can't rely on pseudo-dealing as webmoney 0
    if (this.confirmOrderForm.valid && this.paymentType !== undefined) {
      this.paymentPageLoading = true

      // Form data
      const { email, fullName, passport, phone } = this.confirmOrderForm.value

      const cId = this.getCID()
      const isEng = this.getIsEng()

      /* RU Passport  / EN Address */
      const passportAddressData = this.group.hasRequiredPassportLicense ? passport.passportAddressData : ''
      const phoneData = phone === null ? null : phone.phone

      let result: any
      try {
        result = await this.cartService.getUrl(
          `/web/v2/default/payment/get-url`,
          this.group.beatmakerId,
          this.paymentType,
          email.email,
          phoneData,
          cId,
          this.widgetId,
          isEng,
          this.paymentSource,
          {
            fullName: fullName.fullName,
            passportData: passportAddressData
          }
        )
      } catch (err) {
        this.notificationService.handleError(
          err.statusText === 'Unknown Error'
            ? 'Проверьте правильность ввода номера телефона'
            : this.lg.confirmationOrderPopup.unknownError
        )
        this.paymentPageLoading = false
        return
      }
      try {
        if (result.resultCode === ResultCode.Success) {
          const { paymentId, altPaymentId } = result.data
          this.cartService.savePaymentIdForGroup(this.group.beatmakerId, paymentId, altPaymentId)
          document.location.href = result.data.url
        } else {
          this.notificationService.handleError(result.messages[0])
          this.paymentPageLoading = false
        }
      } catch (err) {
        this.notificationService.handleError(
          err.statusText === 'Unknown Error'
            ? 'Проверьте правильность ввода номера телефона'
            : this.lg.confirmationOrderPopup.unknownError
        )
        this.paymentPageLoading = false
      }
    } else {
      this.notificationService.handleError(
        this.paymentType ? this.lg.forms.error : this.lg.confirmationOrderPopup.paymentSystem
      )
    }
  }

  getCID() {
    let cId = this.cidService.getCID()
    if (!cId) {
      this.cidService.ensureCIDExists()
      cId = this.cidService.getCID()
    }
    return cId
  }
}
